import { z } from 'zod';
import { createPaginatedResponseSchema, IdParamsSchema } from '../common/index.js';
export const TrackEventSchema = z.object({
    event_id: z.string().optional(),
    customer_id: z.string().min(1),
    agent_id: z.string().min(1),
    signal_code: z.string().optional(),
    action_type: z.enum(['task', 'workflow', 'outcome']),
    quantity: z.number().positive().default(1),
    attributes: z.record(z.unknown()).default({}),
    timestamp: z.string().datetime().optional(),
    metadata: z.record(z.unknown()).optional(),
});
export const BatchTrackEventsSchema = z.object({
    events: z.array(TrackEventSchema).min(1).max(100),
});
export const TrackEventResponseSchema = z.object({
    event_id: z.string(),
    status: z.enum(['accepted', 'duplicate']),
    accepted_at: z.string().datetime(),
});
export const BatchTrackResponseSchema = z.object({
    accepted: z.number(),
    duplicates: z.number(),
    results: z.array(TrackEventResponseSchema),
});
export const CreateEventSchema = z.object({
    event_name: z.string().min(1).max(255),
    agent_id: z.string().uuid(),
    customer_id: z.string().uuid().optional(),
    session_id: z.string().max(255).optional(),
    properties: z
        .record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]))
        .optional(),
    timestamp: z.string().datetime().optional(),
    value: z.number().min(0).optional(),
    metadata: z
        .record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]))
        .optional(),
});
export const BatchEventsSchema = z.object({
    events: z.array(CreateEventSchema).min(1).max(1000),
});
export const EventResponseSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    event_name: z.string(),
    agent_id: z.string(),
    customer_id: z.string().nullable().optional(),
    session_id: z.string().nullable().optional(),
    properties: z
        .record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]))
        .nullable()
        .optional(),
    timestamp: z
        .union([z.string(), z.date()])
        .transform(v => (v instanceof Date ? v.toISOString() : v)),
    value: z.number().nullable().optional(),
    metadata: z
        .record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]))
        .nullable()
        .optional(),
    processed: z.boolean().optional(),
    created_at: z
        .union([z.string(), z.date()])
        .transform(v => (v instanceof Date ? v.toISOString() : v)),
    updated_at: z
        .union([z.string(), z.date()])
        .transform(v => (v instanceof Date ? v.toISOString() : v)),
    workspace_id: z.string().nullable().optional(),
    environment: z.string().optional(),
    event_id: z.string().nullable().optional(),
    action_type: z.string().nullable().optional(),
    quantity: z
        .union([z.number(), z.string()])
        .transform(v => (typeof v === 'string' ? parseFloat(v) : v))
        .optional(),
    attributes: z
        .record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]))
        .nullable()
        .optional(),
    status: z.string().optional(),
});
export const EventQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20).optional(),
    event_name: z.string().optional(),
    agent_id: z.string().uuid().optional(),
    customer_id: z.string().uuid().optional(),
    session_id: z.string().optional(),
    from_date: z.string().datetime().optional(),
    to_date: z.string().datetime().optional(),
    processed: z.coerce.boolean().optional(),
    sort: z
        .enum(['timestamp', 'event_name', 'created_at'])
        .default('timestamp')
        .optional(),
    order: z.enum(['asc', 'desc']).default('desc').optional(),
});
export const EventSearchSchema = z.object({
    query: z.object({
        event_name: z.string().optional(),
        agent_id: z.string().uuid().optional(),
        customer_id: z.string().uuid().optional(),
        session_id: z.string().optional(),
        date_range: z
            .object({
            from: z.string().datetime(),
            to: z.string().datetime(),
        })
            .optional(),
        properties: z
            .record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]))
            .optional(),
        value_range: z
            .object({
            min: z.number().optional(),
            max: z.number().optional(),
        })
            .optional(),
    }),
    aggregations: z
        .array(z.object({
        field: z.string(),
        type: z.enum(['count', 'sum', 'avg', 'min', 'max']),
    }))
        .optional(),
    page: z.number().int().min(1).default(1).optional(),
    limit: z.number().int().min(1).max(100).default(20).optional(),
    sort: z
        .enum(['timestamp', 'event_name', 'value'])
        .default('timestamp')
        .optional(),
    order: z.enum(['asc', 'desc']).default('desc').optional(),
});
export const EventStreamSchema = z.object({
    event_name: z.string().optional(),
    agent_id: z.string().uuid().optional(),
    customer_id: z.string().uuid().optional(),
    from_timestamp: z.string().datetime().optional(),
});
export const PaginatedEventsResponseSchema = createPaginatedResponseSchema(EventResponseSchema);
export const BatchEventResponseSchema = z.object({
    success_count: z.number(),
    error_count: z.number(),
    errors: z
        .array(z.object({
        index: z.number(),
        error: z.string(),
    }))
        .optional(),
});
export const EventParamsSchema = IdParamsSchema;
export const SimulateEventsSchema = z.object({
    count: z.number().int().min(1).max(500),
});
export const SimulateEventsResponseSchema = z.object({
    simulated: z.number(),
    agent_count: z.number(),
    customer_count: z.number(),
    skipped: z.number(),
});
export const ClearEventsResponseSchema = z.object({
    cleared: z.number(),
});
//# sourceMappingURL=index.js.map